import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import { PlatoMark, PlatoChip } from "@ats/src/components/shared/PlatoMark";

// The base accent gradient is the DS token `--accent-gradient` (not in theme.ts, which is
// monochrome). Kept as a local constant for the "fill" variant; map to your accent token if you
// expose one. Lightest base gradient, never the deeper / 3-stop variants.
const ACCENT_GRADIENT = "linear-gradient(120deg, #FBD7FF 10%, #FFDEC1 90%)";

/**
 * PlatoCtaButton — the Overview-header entry point into the Plato tab.
 *
 * Reads as a navigation affordance, NOT a solid action button: a quiet outlined control with
 * the gradient Plato chip carrying the accent, the label, and a trailing chevron that signals
 * it takes you to the Plato tab. Use it ONLY for the Plato entry point.
 *
 *   <PlatoCtaButton reviewed={false} onClick={goToPlatoTab} />   → "Ask Plato"
 *   <PlatoCtaButton reviewed onClick={goToPlatoTab} />           → "View Plato review"
 */
export interface PlatoCtaButtonProps {
  /** true once a review exists → "View Plato review"; false → "Ask Plato". */
  reviewed?: boolean;
  /** "outlined" (default) = quiet bordered nav affordance w/ chevron; "fill" = accent-gradient capsule. */
  variant?: "outlined" | "fill";
  /** Overrides the reviewed-derived label. */
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

const PlatoCtaButton: React.FunctionComponent<PlatoCtaButtonProps> = ({ reviewed, variant = "outlined", label, onClick, disabled }) => {
  const text = label || (reviewed ? "View Plato review" : "Ask Plato");
  const fill = variant === "fill";
  return (
    <Styled.Button type="button" data-variant={variant} onClick={onClick} disabled={disabled}>
      {fill ? <PlatoMark size={15} variant="sparkle" /> : <PlatoChip size={22} />}
      {text}
      {!fill && (
        <Styled.Chevron>
          <Icon name="chevron-right" />
        </Styled.Chevron>
      )}
    </Styled.Button>
  );
};

export default PlatoCtaButton;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Chevron = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoCtaButton_Chevron;
    display: inline-flex;
    align-items: center;
    margin-left: 0;
    color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
    transition: transform 0.15s ease;

    svg {
      display: block;
      width: 16px;
      height: 16px;
    }
  `;
});

Styled.Button = styled.button((props: any) => {
  const t: any = props.theme;
  const fill = props["data-variant"] === "fill";
  return css`
    label: PlatoCtaButton;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[2]};
    flex-shrink: 0;
    ${[t.text.sm, t.text.semibold]}
    white-space: nowrap;
    cursor: pointer;

    ${fill
      ? css`
          /* Fill (F4) — accent-gradient capsule, no border, no chevron. */
          height: 32px;
          padding: 0 15px 0 11px;
          border: none;
          border-radius: 999px;
          background-image: ${ACCENT_GRADIENT};
          color: ${t.color.gray[900]};
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.07);
          transition: filter 0.18s ease, box-shadow 0.2s ease;
          &:hover {
            filter: saturate(1.06) brightness(1.02);
            box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          &:focus-visible {
            outline: none;
            box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 0 3px ${t.color.gray[300]};
          }
        `
      : css`
          /* Outlined — taller so the chip has equal vertical padding; quiet bordered nav affordance. */
          height: 36px;
          padding: 0 ${t.spacing[2]};
          ${t.rounded.sm}
          border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
          background: ${t.dark ? t.color.gray[800] : t.color.white};
          color: ${t.dark ? t.color.gray[200] : t.color.black};
          transition: border-color 0.18s ease, box-shadow 0.2s ease;
          &:hover {
            border-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
            box-shadow: 0 1px 3px ${t.dark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.06)"};
          }
          &:hover ${Styled.Chevron} {
            transform: translateX(1px);
          }
          &:focus-visible {
            outline: none;
            border-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
            box-shadow: 0 0 0 3px ${t.dark ? t.color.gray[700] : t.color.gray[200]};
          }
        `}

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
      ${Styled.Chevron} {
        transition: none;
      }
    }
  `;
});
